import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useFirebase } from '../../../context/FirebaseContext';
import { getUserApiKeys, saveUserApiKey, deleteUserApiKey, getUserApiKeyStatus } from '../../../api/user';
import CloseIcon from '@mui/icons-material/Close';
import logger from '../../../utils/logger';
import './ApiKeySettingsModal.css';

const PROVIDERS = [
    { id: 'openai', name: 'OpenAI', placeholder: 'sk-...' },
    { id: 'anthropic', name: 'Anthropic', placeholder: 'sk-ant-...' },
];

const ApiKeySettingsModal = ({ isOpen, onClose }) => {
    const { auth } = useFirebase();
    const [keys, setKeys] = useState({});
    const [status, setStatus] = useState({});
    const [inputs, setInputs] = useState({});
    const [saving, setSaving] = useState({});
    const [messages, setMessages] = useState({});

    const loadKeys = useCallback(async () => {
        if (!auth?.currentUser) return;
        getUserApiKeys(auth, (data) => {
            if (data && !data.error) {
                const keyMap = {};
                data.forEach(k => { keyMap[k.provider] = k; });
                setKeys(keyMap);
            }
        });
        getUserApiKeyStatus(auth, (data) => {
            if (data && !data.error) {
                setStatus(data);
            }
        });
    }, [auth]);

    useEffect(() => {
        if (isOpen) {
            setInputs({});
            setMessages({});
            setSaving({});
            loadKeys();
        }
    }, [isOpen, loadKeys]);

    if (!isOpen) return null;

    const handleSave = async (provider) => {
        const rawKey = inputs[provider];
        if (!rawKey || !rawKey.trim()) {
            setMessages({ ...messages, [provider]: { type: 'error', text: 'Please enter an API key' } });
            return;
        }

        setSaving({ ...saving, [provider]: true });
        setMessages({ ...messages, [provider]: null });

        saveUserApiKey(auth, (data) => {
            setSaving(prev => ({ ...prev, [provider]: false }));
            if (data?.error) {
                setMessages(prev => ({ ...prev, [provider]: { type: 'error', text: data.error } }));
            } else {
                setMessages(prev => ({ ...prev, [provider]: { type: 'success', text: 'API key saved' } }));
                setInputs(prev => ({ ...prev, [provider]: '' }));
                loadKeys();
                setTimeout(() => {
                    setMessages(prev => ({ ...prev, [provider]: null }));
                }, 3000);
            }
        }, provider, rawKey.trim());
    };

    const handleDelete = async (provider) => {
        setSaving({ ...saving, [provider]: true });
        deleteUserApiKey(auth, (data) => {
            setSaving(prev => ({ ...prev, [provider]: false }));
            if (data?.error) {
                setMessages(prev => ({ ...prev, [provider]: { type: 'error', text: data.error } }));
            } else {
                setMessages(prev => ({ ...prev, [provider]: { type: 'success', text: 'API key removed' } }));
                setKeys(prev => {
                    const updated = { ...prev };
                    delete updated[provider];
                    return updated;
                });
                loadKeys();
                setTimeout(() => {
                    setMessages(prev => ({ ...prev, [provider]: null }));
                }, 3000);
            }
        }, provider);
    };

    return createPortal(
        <div className="apikey-modal-overlay" onClick={onClose}>
            <div className="apikey-modal-container" onClick={e => e.stopPropagation()}>
                <div className="apikey-modal-header">
                    <h2 className="apikey-modal-title">API Keys</h2>
                    <button className="apikey-modal-close" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </button>
                </div>

                <div className="apikey-modal-content">
                    <p className="apikey-info-text">
                        An OpenAI API key is required to use AI-powered features like LLM validation rules and chat.
                        Your keys are validated on save, encrypted, and stored securely.
                    </p>

                    {PROVIDERS.map(provider => {
                        const savedKey = keys[provider.id];
                        const providerStatus = status[provider.id];
                        const msg = messages[provider.id];

                        return (
                            <div key={provider.id} className="apikey-provider-section">
                                <div className="apikey-provider-header">
                                    <span className="apikey-provider-name">{provider.name}</span>
                                    <span className={`apikey-provider-badge ${savedKey ? 'saved' : 'not-saved'}`}>
                                        {savedKey ? 'Configured' : 'Required'}
                                    </span>
                                </div>

                                {savedKey && (
                                    <span className="apikey-hint">
                                        Current key: {savedKey.keyHint}
                                    </span>
                                )}

                                <div className="apikey-input-row">
                                    <input
                                        type="password"
                                        className="apikey-input"
                                        placeholder={savedKey ? 'Enter new key to replace' : provider.placeholder}
                                        value={inputs[provider.id] || ''}
                                        onChange={(e) => setInputs({ ...inputs, [provider.id]: e.target.value })}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleSave(provider.id); }}
                                        disabled={saving[provider.id]}
                                    />
                                    <button
                                        className="apikey-btn-save"
                                        onClick={() => handleSave(provider.id)}
                                        disabled={saving[provider.id] || !inputs[provider.id]?.trim()}
                                    >
                                        {saving[provider.id] ? '...' : 'Save'}
                                    </button>
                                    {savedKey && (
                                        <button
                                            className="apikey-btn-delete"
                                            onClick={() => handleDelete(provider.id)}
                                            disabled={saving[provider.id]}
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>

                                {msg && (
                                    <div className={`apikey-message ${msg.type}`}>
                                        {msg.text}
                                    </div>
                                )}

                                {!savedKey && (
                                    <div className="apikey-info-text">
                                        Add your {provider.name} key to use {provider.name} models.
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="apikey-modal-footer">
                    <button className="apikey-btn-close" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ApiKeySettingsModal;
